import { html, nothing } from 'lit';
import { get, translate } from 'lit-translate';
import { appState } from '../app.state';
import { actionRegistry } from '../actions/action-registry';
import { ActionIds } from '../actions/action.types';
import { CommandName } from '../commands/command-name.enum';
import { hasPrivilege } from '../helpers/privilege.helper';
import { ISSUE_TYPE_PUBLIC_HOLIDAY, ISSUE_TYPE_VACATION } from '../constants';
import { isTypeListLoadedFor, offerableTypesOf } from '../domain/helpers/issue-type-root.helper';
import { loadedIssueTypes } from '../helpers/loaded-issue-types.helper';
import type { PopoverListItem } from '@o2d/popover-list';
import type { TimelineToolbarItem } from '../types/timeline-toolbar-item.interface';

/**
 * Whether an issue can be created for the given root (D48): its root offers a type — the root itself
 * unless abstract or deleted, or a live child. While the type list is not loaded yet (the preload
 * after login is not awaited) the answer is yes, so the entry cannot stay greyed out by a race; the
 * action and the backend decide then.
 */
function hasTypeToCreateOn(rootUid: string, types: ReturnType<typeof loadedIssueTypes>): boolean {
    return !isTypeListLoadedFor(rootUid, types) || offerableTypesOf(rootUid, types).length > 0;
}

/**
 * Generates submenu items for the Add Entity menu.
 *
 * Exported for unit testing. Authorization uses synchronous hasPrivilege()
 * checks against appState.privileges (no async HTTP calls, no until() directive).
 *
 * @returns Array of PopoverListItem with current authorization state.
 */
export function getAddEntityMenuItems(): PopoverListItem[] {
    const canCreateIssue = hasPrivilege(CommandName.CREATE_ISSUE);
    const canCreateProject = hasPrivilege(CommandName.CREATE_PROJECT);
    const canCreateEmployee = hasPrivilege(CommandName.CREATE_EMPLOYEE);
    const canCreateKeyword = hasPrivilege(CommandName.CREATE_KEYWORD);
    const canCreateIssueType = hasPrivilege(CommandName.CREATE_ISSUE_TYPE);
    const canCreateIssueStatus = hasPrivilege(CommandName.CREATE_ISSUE_STATUS);
    const types = loadedIssueTypes();
    const canCreateOn = (rootUid: string) => canCreateIssue && hasTypeToCreateOn(rootUid, types);

    return [
        {
            id: 'add-issue',
            label: get('ha-boards-view.add-entity.add-issue'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_ISSUE),
            disabled: !canCreateIssue,
        },
        {
            id: 'add-public-holiday',
            label: get('ha-boards-view.add-entity.add-public-holiday'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_PUBLIC_HOLIDAY),
            disabled: !canCreateOn(ISSUE_TYPE_PUBLIC_HOLIDAY),
        },
        {
            id: 'add-vacancy',
            label: get('ha-boards-view.add-entity.add-vacancy'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_VACATION),
            disabled: !canCreateOn(ISSUE_TYPE_VACATION),
        },
        {
            id: 'add-project',
            label: get('ha-boards-view.add-entity.add-project'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_PROJECT),
            disabled: !canCreateProject,
        },
        {
            id: 'add-daily',
            label: get('ha-boards-view.add-entity.add-daily'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_DAILY),
            disabled: !canCreateProject,
        },
        {
            id: 'add-employee',
            label: get('ha-boards-view.add-entity.add-employee'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_EMPLOYEE),
            disabled: !canCreateEmployee,
        },
        {
            id: 'add-keyword',
            // NOTE: i18n key is 'add-label' (not 'add-keyword') — per PRD spec and legacy
            label: get('ha-boards-view.add-entity.add-label'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_KEYWORD),
            disabled: !canCreateKeyword,
        },
        {
            id: 'add-issue-type',
            // NOTE: i18n key is 'add-type' (not 'add-issue-type') — per PRD spec and legacy
            label: get('ha-boards-view.add-entity.add-type'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_ISSUE_TYPE),
            disabled: !canCreateIssueType,
        },
        {
            id: 'add-issue-status',
            label: get('ha-boards-view.add-entity.add-status'),
            type: 'item',
            payload: () => actionRegistry.execute(ActionIds.DIALOG_NEW_ISSUE_STATUS),
            disabled: !canCreateIssueStatus,
        },
    ];
}

/**
 * Add Entity Menu toolbar item for o2d timeline views.
 *
 * Renders a ha-button-with-submenu with 9 entity creation options.
 * Authorization is checked synchronously via hasPrivilege() (no async render).
 * Includes a login guard: renders nothing when the user is not logged in.
 */
export const addEntityMenuToolbarItem: TimelineToolbarItem = {
    id: 'add-entity-menu',
    position: 'start',
    order: 20,
    render: () => {
        if (!appState.isUserLoggedIn) {
            return nothing;
        }

        return html`
            <ha-button-with-submenu
                .items=${getAddEntityMenuItems()}
                button-title=${translate('mainToolbar.createNew')}
                class="add-entity buttonform-marker primary"
                submenu-layout="buttons">
            </ha-button-with-submenu>
        `;
    },
};
